import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { ChevronRight, Check, TrendingUp, ArrowUp, ArrowRight, ArrowDown, Link2Off, Hammer, Shield, Coins, TrendingUp as ChartUp, Unlock, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Stage Icons mapping
const STAGE_ICONS = {
  supported: Link2Off,      // Broken chain
  builder: Hammer,          // Hammer for productivity
  stabilizer: Shield,       // Shield for stability
  accumulator: Coins,       // Coins for accumulation
  investor: ChartUp,        // Trending chart for growth
  lifeOwner: Unlock,        // Open lock for freedom
  legacyDesigner: Crown,    // Crown for abundance/legacy
};

// Wealth Stage definitions - we'll use translation keys for names
const WEALTH_STAGES = [
  { key: 'supported', nameKey: 'preLoginQuiz.stages.supported', level: 1, color: '#DC2626' },
  { key: 'builder', nameKey: 'preLoginQuiz.stages.builder', level: 2, color: '#EA580C' },
  { key: 'stabilizer', nameKey: 'preLoginQuiz.stages.stabilizer', level: 3, color: '#F59E0B' },
  { key: 'accumulator', nameKey: 'preLoginQuiz.stages.accumulator', level: 4, color: '#84CC16' },
  { key: 'investor', nameKey: 'preLoginQuiz.stages.investor', level: 5, color: '#059669' },
  { key: 'lifeOwner', nameKey: 'preLoginQuiz.stages.lifeOwner', level: 6, color: '#1E3A8A' },
  { key: 'legacyDesigner', nameKey: 'preLoginQuiz.stages.legacyDesigner', level: 7, color: '#8B5CF6' },
];

// Direction definitions - we'll use translation keys for labels
const DIRECTIONS = {
  advancing: { key: 'advancing', labelKey: 'preLoginQuiz.advancing', icon: ArrowUp, color: '#10B981', symbol: '↑' },
  steady: { key: 'steady', labelKey: 'preLoginQuiz.steady', icon: ArrowRight, color: '#9CA3AF', symbol: '→' },
  contracting: { key: 'contracting', labelKey: 'preLoginQuiz.declining', icon: ArrowDown, color: '#EF4444', symbol: '↓' },
};

// Function to get questions with translations
const getQuestions = (t) => [
  {
    id: 1,
    question: t('preLoginQuiz.q1'),
    options: [
      { id: 'depend', label: t('preLoginQuiz.q1_opt1'), value: 1 },
      { id: 'savings', label: t('preLoginQuiz.q1_opt2'), value: 2 },
      { id: 'partial', label: t('preLoginQuiz.q1_opt3'), value: 3 },
      { id: 'full', label: t('preLoginQuiz.q1_opt4'), value: 4 },
      { id: 'surplus', label: t('preLoginQuiz.q1_opt5'), value: 5 }
    ]
  },
  {
    id: 2,
    question: t('preLoginQuiz.q2'),
    options: [
      { id: 'no', label: t('preLoginQuiz.q2_opt1'), value: 1 },
      { id: 'barely', label: t('preLoginQuiz.q2_opt2'), value: 2 },
      { id: 'yes', label: t('preLoginQuiz.q2_opt3'), value: 3 },
      { id: 'surplus', label: t('preLoginQuiz.q2_opt4'), value: 4 }
    ]
  },
  {
    id: 3,
    question: t('preLoginQuiz.q3'),
    options: [
      { id: 'less_1', label: t('preLoginQuiz.q3_opt1'), value: 1 },
      { id: '1_3', label: t('preLoginQuiz.q3_opt2'), value: 2 },
      { id: '3_12', label: t('preLoginQuiz.q3_opt3'), value: 3 },
      { id: 'more_1yr', label: t('preLoginQuiz.q3_opt4'), value: 4 },
      { id: 'indefinite', label: t('preLoginQuiz.q3_opt5'), value: 5 }
    ]
  }
];

// Analysis phrases - using translation function
const getAnalysisPhrases = (t) => [
  t('preLoginQuiz.analysis1'),
  t('preLoginQuiz.analysis2'),
  t('preLoginQuiz.analysis3'),
  t('preLoginQuiz.analysis4')
];

// Calculate Stage (same logic as original)
const calculateStage = (q1, q2, q3) => {
  if (q1 === 1) return 'supported';
  if (q1 === 2) {
    if ((q2 === 1 || q2 === 2) && q3 <= 2) return 'builder';
    return 'stabilizer';
  }
  if (q1 === 3) {
    if ((q2 === 3 || q2 === 4) && q3 >= 3) return 'investor';
    return 'accumulator';
  }
  if (q1 === 4) return 'lifeOwner';
  if (q1 === 5) return 'legacyDesigner';
  return 'builder';
};

// Calculate Direction (same logic as original)
const calculateDirection = (q2, q3) => {
  if ((q2 === 3 || q2 === 4) && q3 >= 3) return 'advancing';
  if (q2 === 2 || q3 === 2) return 'steady';
  if (q2 === 1 || q3 === 1) return 'contracting';
  return 'steady';
};

// Pre-Login Quiz Component
const PreLoginQuiz = ({ onComplete, onClose }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisPhrase, setAnalysisPhrase] = useState(0);

  // Get translated questions and phrases
  const QUESTIONS = getQuestions(t);
  const ANALYSIS_PHRASES = getAnalysisPhrases(t);

  const handleOptionSelect = (optionValue) => {
    setSelectedOption(optionValue);
  };

  const handleNext = () => {
    if (selectedOption === null) return;

    const newAnswers = { ...answers, [QUESTIONS[currentQuestion].id]: selectedOption };
    setAnswers(newAnswers);

    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
    } else {
      // Start analysis
      setIsAnalyzing(true);
      
      // Animate through analysis phrases
      let phraseIndex = 0;
      const phraseInterval = setInterval(() => {
        phraseIndex++;
        if (phraseIndex < ANALYSIS_PHRASES.length) {
          setAnalysisPhrase(phraseIndex);
        } else {
          clearInterval(phraseInterval);
          
          // Calculate results
          const stage = calculateStage(newAnswers[1], newAnswers[2], newAnswers[3]);
          const direction = calculateDirection(newAnswers[2], newAnswers[3]);
          const stageInfo = WEALTH_STAGES.find(s => s.key === stage);
          
          const result = {
            stage_key: stage,
            stage_name: t(stageInfo.nameKey),
            stage_level: stageInfo.level,
            direction: direction,
            answers: newAnswers,
            timestamp: new Date().toISOString()
          };
          
          // Store in session storage for persistence across login
          sessionStorage.setItem('preLoginQuizResult', JSON.stringify(result));
          
          // Call completion handler
          onComplete(result);
        }
      }, 600);
    }
  };

  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  if (isAnalyzing) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-2xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-xl text-white font-medium animate-pulse">
            {ANALYSIS_PHRASES[analysisPhrase]}
          </p>
        </div>
      </div>
    );
  }

  const question = QUESTIONS[currentQuestion];

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-yellow-500" />
          <span className="text-sm text-gray-400">Financial Pulse Check</span>
        </div>
        <span className="text-sm text-gray-500">
          Question {currentQuestion + 1} of {QUESTIONS.length}
        </span>
      </div>

      {/* Progress Bar */}
      <Progress value={progress} className="h-2 mb-8" />

      {/* Question */}
      <h2 className="text-xl md:text-2xl font-bold text-white mb-8">
        {question.question}
      </h2>

      {/* Options */}
      <div className="space-y-3 mb-8">
        {question.options.map((option) => (
          <button
            key={option.id}
            onClick={() => handleOptionSelect(option.value)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
              selectedOption === option.value
                ? 'border-yellow-500 bg-yellow-500/10 text-white'
                : 'border-gray-700 hover:border-gray-600 text-gray-300 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedOption === option.value
                  ? 'border-yellow-500 bg-yellow-500'
                  : 'border-gray-600'
              }`}>
                {selectedOption === option.value && (
                  <Check className="w-4 h-4 text-black" />
                )}
              </div>
              <span className="text-sm md:text-base">{option.label}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-300 text-sm"
        >
          {t('preLoginQuiz.cancel')}
        </button>
        <Button
          onClick={handleNext}
          disabled={selectedOption === null}
          className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 disabled:opacity-50"
        >
          {currentQuestion === QUESTIONS.length - 1 ? t('preLoginQuiz.seeResults') : t('preLoginQuiz.continue')}
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Quiz Result Teaser (shown before login)
const QuizResultTeaser = ({ result, onSaveResult }) => {
  const { t } = useTranslation();
  const stageInfo = WEALTH_STAGES.find(s => s.key === result.stage_key);
  const directionInfo = DIRECTIONS[result.direction];
  const StageIcon = STAGE_ICONS[result.stage_key];

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-2xl">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{t('preLoginQuiz.assessmentComplete')}</h2>
        <p className="text-gray-400">{t('preLoginQuiz.positionEvaluated')}</p>
      </div>

      {/* Stage Display */}
      <div className="text-center mb-8">
        <p className="text-sm text-gray-400 uppercase tracking-wider mb-4">{t('preLoginQuiz.currentStage')}</p>
        
        <div 
          className="inline-block rounded-2xl p-6 mb-4"
          style={{ 
            backgroundColor: stageInfo.color,
            boxShadow: `0 0 40px ${stageInfo.color}40`
          }}
        >
          <div className="flex flex-col items-center">
            <StageIcon className="w-10 h-10 text-white/90 mb-2" />
            <span className="text-3xl font-bold text-white">{t(stageInfo.nameKey)}</span>
          </div>
        </div>

        {/* Direction Badge */}
        <div className="flex justify-center">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ 
              backgroundColor: `${directionInfo.color}20`,
              color: directionInfo.color
            }}
          >
            <directionInfo.icon className="w-4 h-4" />
            <span className="font-semibold">{directionInfo.symbol} {t(directionInfo.labelKey)}</span>
          </div>
        </div>
      </div>

      {/* Teaser Message */}
      <div className="bg-gray-800/50 rounded-xl p-6 mb-8 text-center">
        <p className="text-lg text-white mb-2">{t('preLoginQuiz.fullBreakdownReady')}</p>
        <p className="text-gray-400 text-sm">
          {t('preLoginQuiz.getPersonalizedInsights')}
        </p>
      </div>

      {/* CTA */}
      <div className="text-center">
        <Button
          onClick={onSaveResult}
          className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          {t('preLoginQuiz.saveAndUnlock')}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
        <p className="text-gray-500 text-xs mt-4">
          {t('preLoginQuiz.freeAccount')}
        </p>
      </div>
    </div>
  );
};

export { PreLoginQuiz, QuizResultTeaser, WEALTH_STAGES, DIRECTIONS, STAGE_ICONS };
export default PreLoginQuiz;
